import { toDateString } from '@seen/core/dates'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { Participant } from '@seen/core/models/patients/participant'
import { Button, TypographyDetail, TypographySubtle } from '@seen/ui'
import { IconArrowBackUp, IconPencil, IconUserMinus } from '@tabler/icons-react'

import { type JSX, useState } from 'react'
import { z } from 'zod'
import { Card } from '#src/components/core/Card.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { PatientCard } from '#src/components/patient/PatientCard.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useDisenrollDialogStore } from '#src/hooks/useDisenrollDialogStore.ts'
import { useBatchMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useResetOnSubmit } from '#src/hooks/useResetOnSubmit.ts'

export function DisenrollmentDateDisplay({
  participant,
}: {
  participant: Participant
}): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const [open, setOpen] = useState(false)
  const batchMutation = useBatchMutation()
  const disenrollmentDateString = participant.disenrollmentDate
    ? toDateString(participant.disenrollmentDate)
    : null

  const form = useForm({
    schema: z.object({
      disenrollmentDate: z.string().nullable(),
    }),
    defaultValues: {
      disenrollmentDate: disenrollmentDateString,
    },
  })
  const disenrollmentDateChanged = useDidChange(disenrollmentDateString)
  if (disenrollmentDateChanged) {
    form.setValue('disenrollmentDate', disenrollmentDateString)
  }

  useResetOnSubmit(form, {
    disenrollmentDate: disenrollmentDateString,
  })

  const onSubmit = form.handleSubmit((values) => {
    batchMutation.mutate(
      (batch) => {
        batch.patch(participant, (p) => {
          p.disenrollmentDate = values.disenrollmentDate
        })
      },
      {
        onSuccess: () => {
          setOpen(false)
        },
      }
    )
  })

  if (!disenrollmentDateString) {
    return null
  }

  return (
    <div className="group flex items-center gap-1">
      <div className="grid flex-1 gap-1">
        <TypographyDetail className="leading-none font-semibold text-zinc-950">
          Disenrollment Date
        </TypographyDetail>
        <TypographySubtle className="py-1.5">
          {disenrollmentDateString}
        </TypographySubtle>
      </div>
      {!isReadOnly && (
        <div className="invisible flex group-hover:visible">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogButton variant="ghost" size="icon">
              <IconPencil className="size-4" />
            </DialogButton>
            <DialogDisplay>
              <DialogHeader>Edit Disenrollment Date</DialogHeader>
              <DialogContent>
                <Form onSubmit={onSubmit} className="grid gap-4">
                  <FormField
                    control={form.control}
                    name="disenrollmentDate"
                    render={({ field }) => <DatePicker {...field} />}
                  />
                  <Button type="submit" loading={batchMutation.isPending}>
                    Save
                  </Button>
                </Form>
              </DialogContent>
            </DialogDisplay>
          </Dialog>
        </div>
      )}
    </div>
  )
}

export function DisenrollDropdownMenuItem({
  participant,
}: {
  participant: Participant
}): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const openDisenrollDialog = useDisenrollDialogStore((state) => state.open)

  if (
    isReadOnly ||
    !participant.enrollmentDate ||
    participant.disenrollmentDate
  ) {
    return null
  }

  return (
    <DropdownMenuItem onSelect={() => openDisenrollDialog(participant)}>
      <IconUserMinus className="size-4" />
      Disenroll…
    </DropdownMenuItem>
  )
}

export function WithdrawEnrollmentDropdownMenuItem({
  participant,
}: {
  participant: Participant
}): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const { open: confirmWithAnnotation } = useConfirmWithAnnotationStore()
  const batchMutation = useBatchMutation()

  if (isReadOnly || !participant.canWithdrawEnrollment()) {
    return null
  }

  const enrollmentDateString = participant.enrollmentDateString
  const defaultNote = enrollmentDateString
    ? `Withdrew enrollment before ${enrollmentDateString}`
    : 'Withdrew enrollment before start date'

  return (
    <DropdownMenuItem
      onSelect={() => {
        confirmWithAnnotation({
          title: 'Withdraw Enrollment',
          description: (
            <div className="flex flex-col gap-4">
              <Card>
                <PatientCard participant={participant} />
              </Card>
              <span>
                Withdrawing enrollment will move this participant back to lead
                status and mark them inactive. Are you sure you want to
                continue?
              </span>
            </div>
          ),
          noteRequired: true,
          defaultNote,
          onConfirm: (note) => {
            batchMutation.mutate((batch) => {
              batch.patch(participant, (p) => {
                p.withdrawEnrollment()
              })

              const timelineNote = new CommentCommunication({
                partOf: [participant.reference],
                message: note.trim(),
                sender: practitioner.reference,
              })
              batch.create(timelineNote)
            })
          },
        })
      }}
    >
      <IconArrowBackUp className="size-4" />
      Withdraw Enrollment…
    </DropdownMenuItem>
  )
}
